<template>
  <div class="suggested-moments">
    <div class="section-heading">
      <div>
        <p class="eyebrow">
          {{ highlights.length ? 'A quick way in' : 'Ceremony suggestions' }}
        </p>
        <h3 id="suggested-moments-heading">Suggested moments</h3>
      </div>
      <span v-if="highlights.length" class="result-count">
        {{ highlights.length }} moment{{ highlights.length === 1 ? '' : 's' }}
      </span>
    </div>

    <div v-if="isLoading" class="moment-rail" aria-label="Loading suggested moments">
      <div v-for="index in 4" :key="index" class="moment-card skeleton" aria-hidden="true">
        <span></span>
      </div>
    </div>

    <div v-else-if="highlights.length" class="moment-rail">
      <button
        v-for="highlight in highlights"
        :key="highlightEventId(highlight)"
        type="button"
        class="moment-card"
        :class="{ active: highlightEventId(highlight) === selectedEventId }"
        @click="$emit('select-highlight', highlight)"
      >
        <div class="thumbnail">
          <video
            v-if="activeVideoUrl"
            muted
            playsinline
            preload="metadata"
            :src="activeVideoUrl"
            @loadedmetadata="seekThumbnail($event, highlight)"
          ></video>
          <div v-else class="thumbnail-placeholder"></div>
          <span
            v-if="highlight.kind === 'standout'"
            class="standout-badge"
          >
            Standout
          </span>
          <span class="duration-badge">{{ durationLabel(highlight) }}</span>
          <span class="play-indicator" aria-hidden="true">▶</span>
        </div>
        <span class="card-copy">
          <strong>{{ highlight.label || 'Ceremony moment' }}</strong>
          <span>{{ highlight.summary || kindLabel(highlight) }}</span>
          <time>{{ startLabel(highlight) }}</time>
        </span>
        <span
          v-if="highlightEventId(highlight) === selectedEventId"
          class="now-playing"
        >
          Now playing
        </span>
      </button>
    </div>

    <div v-else-if="readiness === 'ready'" class="empty-state">
      <span class="empty-icon" aria-hidden="true">✦</span>
      <div>
        <strong>No standout moments suggested</strong>
        <p>That’s completely normal. Browse the ceremony guide or search for a specific moment.</p>
      </div>
    </div>

    <div v-else class="empty-state quiet">
      <span class="empty-icon" aria-hidden="true">○</span>
      <div>
        <strong>Suggested moments are not ready yet</strong>
        <p>The ceremony guide and suggestions will appear after preparation.</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CeremonyHighlights',
  props: {
    highlights: {
      type: Array,
      default: () => [],
    },
    selectedEventId: {
      type: String,
      default: '',
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    activeVideoUrl: {
      type: String,
      default: '',
    },
    formatDuration: {
      type: Function,
      required: true,
    },
    readiness: {
      type: String,
      default: 'idle',
    },
  },
  methods: {
    timestamp(highlight) {
      const value = Number(
        highlight?.best_frame_timestamp_sec
        ?? highlight?.start_second
      )
      return Number.isFinite(value) ? value : 0
    },
    durationSeconds(highlight) {
      const duration = Number(highlight?.duration_seconds)
      if (Number.isFinite(duration) && duration > 0) return duration
      return Math.max(
        1,
        Number(highlight?.end_second || 0) - Number(highlight?.start_second || 0)
      )
    },
    highlightEventId(highlight) {
      const identity = String(
        highlight?.scene_id
        ?? highlight?.best_key
        ?? highlight?.id
        ?? this.timestamp(highlight)
      )
      return `highlight-${identity}`
    },
    startLabel(highlight) {
      return this.formatDuration(this.timestamp(highlight))
    },
    durationLabel(highlight) {
      return this.formatDuration(this.durationSeconds(highlight))
    },
    kindLabel(highlight) {
      return highlight.kind === 'ceremony_moment'
        ? 'A representative ceremony moment'
        : 'A suggested moment from this ceremony'
    },
    seekThumbnail(event, highlight) {
      const video = event.target
      const duration = Number(video.duration)
      const requested = this.timestamp(highlight)
      if (Number.isFinite(duration) && duration > 0) {
        video.currentTime = Math.min(requested, Math.max(0, duration - 0.1))
      }
    },
  },
}
</script>

<style scoped>
.suggested-moments {
  display: grid;
  gap: 14px;
}

.section-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
}

.eyebrow {
  margin: 0 0 3px;
  color: #7c3aed;
  font-size: 0.7rem;
  font-weight: 750;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

h3 {
  margin: 0;
  color: #172033;
  font-size: 1.05rem;
  font-weight: 720;
}

.result-count {
  color: #667085;
  font-size: 0.75rem;
}

.moment-rail {
  display: grid;
  grid-auto-columns: minmax(260px, 310px);
  grid-auto-flow: column;
  gap: 12px;
  overflow-x: auto;
  padding: 2px 2px 8px;
  scroll-snap-type: x proximity;
}

.moment-card {
  position: relative;
  display: grid;
  min-width: 0;
  overflow: hidden;
  border: 1px solid #e2e6ed;
  border-radius: 14px;
  background: #fff;
  text-align: left;
  scroll-snap-align: start;
  transition: border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease;
}

.moment-card:hover {
  border-color: #bba9ea;
  transform: translateY(-1px);
}

.moment-card.active {
  border-color: #7c3aed;
  box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.14);
}

.thumbnail {
  position: relative;
  aspect-ratio: 16 / 8.8;
  overflow: hidden;
  background: #111827;
}

.thumbnail video,
.thumbnail-placeholder {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-placeholder {
  background: linear-gradient(135deg, #2f2850, #151225);
}

.standout-badge,
.duration-badge,
.play-indicator {
  position: absolute;
  z-index: 1;
}

.standout-badge {
  top: 9px;
  left: 9px;
  border-radius: 999px;
  padding: 4px 7px;
  background: rgba(124, 58, 237, 0.92);
  color: #fff;
  font-size: 0.62rem;
  font-weight: 750;
  text-transform: uppercase;
}

.duration-badge {
  top: 9px;
  right: 9px;
  border-radius: 6px;
  padding: 4px 6px;
  background: rgba(8, 11, 20, 0.8);
  color: #fff;
  font-size: 0.68rem;
  font-variant-numeric: tabular-nums;
}

.play-indicator {
  top: 50%;
  left: 50%;
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  border-radius: 50%;
  background: rgba(15, 18, 31, 0.72);
  color: #fff;
  font-size: 0.8rem;
  transform: translate(-50%, -50%);
}

.card-copy {
  display: grid;
  gap: 4px;
  padding: 11px 12px 13px;
}

.card-copy strong {
  overflow-wrap: anywhere;
  color: #1d2939;
  font-size: 0.83rem;
  line-height: 1.35;
}

.card-copy span {
  display: -webkit-box;
  overflow: hidden;
  color: #667085;
  font-size: 0.7rem;
  line-height: 1.4;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.card-copy time {
  color: #7c3aed;
  font-size: 0.7rem;
  font-weight: 700;
}

.now-playing {
  position: absolute;
  right: 10px;
  bottom: 10px;
  color: #6941c6;
  font-size: 0.64rem;
  font-weight: 750;
}

.empty-state {
  display: flex;
  align-items: center;
  gap: 13px;
  min-height: 84px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 16px 18px;
  background: #fbfcfe;
}

.empty-state.quiet {
  background: #f8f9fb;
}

.empty-icon {
  display: grid;
  width: 38px;
  height: 38px;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 50%;
  background: #f0eaff;
  color: #7c3aed;
}

.empty-state strong {
  color: #344054;
  font-size: 0.84rem;
}

.empty-state p {
  margin: 3px 0 0;
  color: #667085;
  font-size: 0.75rem;
}

.skeleton {
  min-height: 190px;
  border-color: transparent;
  background: linear-gradient(90deg, #eef0f4 0%, #f8f9fb 50%, #eef0f4 100%);
  background-size: 200% 100%;
  animation: shimmer 1.2s ease-in-out infinite;
}

@keyframes shimmer {
  to {
    background-position: -200% 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .moment-card,
  .skeleton {
    animation: none;
    transition: none;
  }
}

@media (max-width: 640px) {
  .moment-rail {
    grid-auto-columns: 78%;
  }
}
</style>
